import {
  changedReportLines,
  type DictationReport,
  type DictationReportGuard,
  type DictationReportInput,
  detectDictationTemplate,
  renderDictationDefaults,
} from "./dictation-report";
import { applySpokenCorrections } from "./dictation-study";

// One dictated report (radiology dictation mode), shared by the web app and
// the Chrome extension. Every Stop hands the final transcript to
// planDictation:
// - a spoken template command ("use my normal abdominal template") picks
//   that template; everything dictated so far is filled into it again;
// - other words fill the template, or, once there is a report, add to or
//   correct it ("add gallbladder sludge") in continuing mode;
// - spoken corrections inside one dictation ("scratch that", "X, I mean Y",
//   "change X to Y") are applied before anything is sent, so the dictation
//   kept and checked is what was meant. The API applies them again (no-op
//   on corrected text) for clients that send raw text.

export type DictationSessionTemplate = {
  id: string;
  name: string;
  aliases: string[];
  body: string;
  // The radiologist's own rules for this template ("" for none).
  rules: string;
};

export type DictationTemplatePick = "voice" | "menu" | "page";

export type DictationReportState<
  T extends DictationSessionTemplate = DictationSessionTemplate,
> = {
  template: T | null;
  // How the template was chosen, for the chip ("by voice", "from the study").
  pickedBy: DictationTemplatePick | null;
  // Dictated text (without command words), oldest first.
  dictations: string[];
  report: string | null;
  // Lines of the report that differ from the template's normal wording.
  changedLines: number[];
  // Lines the last fill changed (shown with a stronger mark).
  lastChangedLines: number[];
  guard: DictationReportGuard | null;
};

export const EMPTY_DICTATION_REPORT: DictationReportState = {
  template: null,
  pickedBy: null,
  dictations: [],
  report: null,
  changedLines: [],
  lastChangedLines: [],
  guard: null,
};

export type DictationStep<
  T extends DictationSessionTemplate = DictationSessionTemplate,
> =
  // Nothing to send: only a template change (or nothing was said).
  | { kind: "state"; state: DictationReportState<T> }
  // Send `input`; apply the answer to `state`.
  | {
      kind: "fill";
      state: DictationReportState<T>;
      input: DictationReportInput;
    };

const joined = (dictations: readonly string[]): string =>
  dictations.join("\n\n");

// The template's own rules go with every fill of it.
const rulesOf = (template: DictationSessionTemplate): { rules?: string } =>
  template.rules.trim() ? { rules: template.rules } : {};

/** Use `template` for everything dictated so far. */
export const withDictationTemplate = <T extends DictationSessionTemplate>(
  template: T,
  pickedBy: DictationTemplatePick,
  dictations: string[],
): DictationStep<T> => {
  const base: DictationReportState<T> = {
    ...(EMPTY_DICTATION_REPORT as DictationReportState<T>),
    template,
    pickedBy,
    dictations,
    report: renderDictationDefaults(template.body),
  };
  if (!dictations.length) return { kind: "state", state: base };
  return {
    kind: "fill",
    state: base,
    input: {
      template: template.body,
      dictation: joined(dictations),
      continuing: false,
      ...rulesOf(template),
    },
  };
};

/** What one final transcript does (pure). */
export const planDictation = <T extends DictationSessionTemplate>(
  state: DictationReportState<T>,
  transcript: string,
  templates: readonly T[],
): DictationStep<T> => {
  const command = detectDictationTemplate(transcript, templates);
  const body = applySpokenCorrections(command.body).text.trim();
  if (command.template && command.template.id !== state.template?.id)
    return withDictationTemplate(
      command.template,
      "voice",
      [...state.dictations, body].filter(Boolean),
    );
  if (!body) return { kind: "state", state };
  const dictations = [...state.dictations, body];
  if (!state.template)
    return { kind: "state", state: { ...state, dictations } };
  const continuing = state.dictations.length > 0 && !!state.report;
  return {
    kind: "fill",
    state: { ...state, dictations },
    input: continuing
      ? {
          template: state.report ?? "",
          dictation: body,
          continuing: true,
          ...rulesOf(state.template),
        }
      : {
          template: state.template.body,
          dictation: joined(dictations),
          continuing: false,
          ...rulesOf(state.template),
        },
  };
};

/** The state after a successful fill. */
export const applyDictationFill = <T extends DictationSessionTemplate>(
  state: DictationReportState<T>,
  result: DictationReport,
): DictationReportState<T> => ({
  ...state,
  report: result.report,
  changedLines: state.template
    ? changedReportLines(state.template.body, result.report)
    : result.changedLines,
  lastChangedLines: result.changedLines,
  guard: result.guard,
});
